import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { Phone, MapPin, Clock, Mail } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact | Blanco Pro Services, Free Estimate" },
      { name: "description", content: "Get in touch with Blanco Pro Services for a free epoxy flooring or hardscaping estimate. Serving New Jersey, Pennsylvania, and New York." },
      { property: "og:title", content: "Contact Blanco Pro Services" },
      { property: "og:description", content: "Free estimates. Call 908-895-6133." },
      { property: "og:url", content: "https://blancopros.com/contact" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Contact Blanco Pro Services" },
      { name: "twitter:description", content: "Free estimates. Call 908-895-6133." },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/contact" }],
  }),
  component: ContactPage,
});

const GHL_FORM_SCRIPT = "https://link.msgsndr.com/js/form_embed.js";

function ContactPage() {
  // Load the GHL embed script once. It auto-resizes the iframe to fit the form.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.querySelector(`script[src="${GHL_FORM_SCRIPT}"]`)) return;
    const s = document.createElement("script");
    s.src = GHL_FORM_SCRIPT;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <>
      <section className="pt-40 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-hero relative overflow-hidden">
        <div className="absolute -top-10 left-10 w-72 h-72 rounded-full bg-gradient-gold blur-3xl opacity-20 animate-float" />
        <div className="relative max-w-5xl mx-auto text-center animate-fade-up">
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Get In Touch</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.95] mb-6">
            Let's build something <em className="not-italic bg-gradient-gold bg-clip-text text-transparent">beautiful</em>.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Send us a message or call directly, we'll respond fast and set up your free estimate.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10">
          {/* CONTACT INFO */}
          <Reveal className="lg:col-span-2">
            <div className="bg-foreground text-background rounded-3xl p-10 h-full shadow-elegant relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gradient-gold blur-3xl opacity-30" />
              <div className="relative">
                <h2 className="font-display text-3xl font-bold mb-2">Contact Info</h2>
                <p className="text-background/60 mb-10">Reach out today.</p>

                <ul className="space-y-6">
                  {[
                    { icon: Phone, label: "Phone", value: "908-895-6133", href: "tel:9088956133" },
                    { icon: Mail, label: "Email", value: "service@blancopros.com", href: "mailto:service@blancopros.com" },
                    { icon: MapPin, label: "Location", value: "Serving New Jersey, Pennsylvania, and New York" },
                    { icon: Clock, label: "Hours", value: "Monday – Saturday\n7:30 AM – 4:30 PM" },
                  ].map((c) => (
                    <li key={c.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-background/10 backdrop-blur flex items-center justify-center shrink-0">
                        <c.icon className="w-4 h-4 text-gold" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-background/60 mb-1">{c.label}</div>
                        {c.href ? (
                          <a href={c.href} className="font-medium hover:text-gold transition whitespace-pre-line">{c.value}</a>
                        ) : (
                          <div className="font-medium whitespace-pre-line">{c.value}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>

          {/* GHL EMBEDDED FORM */}
          <Reveal delay={150} className="lg:col-span-3">
            <div className="bg-card border border-border rounded-3xl p-6 sm:p-8 shadow-soft">
              <h2 className="font-display text-3xl font-bold mb-2">Request a Quote</h2>
              <p className="text-muted-foreground mb-6">Tell us about your project, we'll be in touch fast.</p>
              <iframe
                src="https://api.leadconnectorhq.com/widget/form/69OJEfIUJAEPSBQjGAcx"
                style={{ width: "100%", minHeight: "600px", border: "none", borderRadius: "12px" }}
                id="inline-69OJEfIUJAEPSBQjGAcx"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Contact Us"
                data-height="undefined"
                data-layout-iframe-id="inline-69OJEfIUJAEPSBQjGAcx"
                data-form-id="69OJEfIUJAEPSBQjGAcx"
                title="Contact Us"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
